// =============================================================================
// fxSprites.ts (src/client/presentation) — Sprites desenhados de projéteis e
// efeitos de habilidade, extraídos das grades de references/ por
// scripts/build_char_sheets.py (seção FX_SPRITES) para /assets/fx/<nome>.png.
// Efeitos animados são strips horizontais de células quadradas (frames > 1).
//
// Mesmo contrato do charSheets: o PNG carrega assíncrono e fxTex()/fxFrames()
// devolvem null até a textura estar pronta — quem consome (Renderer) usa o
// sprite procedural ou o desenho vetorial como fallback nesse meio tempo.
//
// Pra adicionar um efeito novo: registre a extração no script Python, rode-o
// e acrescente {name, frames} em FX_DEFS abaixo.
//
// PIXI é um global (carregado via <script> em /vendor); os tipos vêm de
// globals.d.ts — o import abaixo é type-only e some no bundle.
// =============================================================================

import type { Texture } from 'pixi.js';

const FX_DEFS = [
  { name: 'cachaca', frames: 1 },
  { name: 'cachaca_explosion', frames: 4 }, // pouso: garrafa -> BOOM -> assentando -> fumaça
  { name: 'urna_monster', frames: 6 },      // urna possuída: ciclo de caminhada frontal
  // Retratos dos bosses (issue #116): estáticos, substituem o procedural
  { name: 'boss_live_19h', frames: 1 },
  { name: 'boss_mensalao2', frames: 1 },
  { name: 'boss_impeachment_dilma', frames: 1 },
  { name: 'boss_cpi_covid', frames: 1 },
  { name: 'boss_stf_bolso', frames: 1 },
  { name: 'boss_orcamento_secreto', frames: 1 },
  { name: 'boss_lula_planalto', frames: 1 },
  { name: 'boss_oito_jan_invasao', frames: 1 },
  // Efeitos das habilidades (spells.png, grade 4x4). O nome bate com o kind do
  // projétil (override automático em projSprite) ou com o efeito que o Renderer
  // consome (raiz/vacina em auras, icms na aura de dreno, soluco/
  // mensalao/explosao/stf nos AoE instantâneos, aliados no PIX).
  { name: 'soluco', frames: 1 },
  { name: 'pao', frames: 1 },
  { name: 'vento', frames: 1 },
  { name: 'cadeira', frames: 1 },
  { name: 'raiz', frames: 1 },
  { name: 'carteira', frames: 1 },
  { name: 'vampiro', frames: 1 },
  { name: 'fake', frames: 1 },
  { name: 'faz_l', frames: 1 },
  { name: 'aliados', frames: 1 },
  { name: 'vacina', frames: 1 },
  // Asset dedicado: vórtice de imposto em pixel art (não vem da grade
  // spells.png). O nome próprio evita que uma regeneração apague o ICMS novo.
  { name: 'icms', frames: 1 },
  // Crucifixo dedicado da Cruz Credo; não é regenerado pela grade spells.png.
  { name: 'cruz_credo', frames: 1 },
  { name: 'mensalao', frames: 1 },
  { name: 'explosao', frames: 1 },
  { name: 'stf', frames: 1 },
] as const;

const textures = new Map<string, Texture[]>();

for (const { name, frames } of FX_DEFS) {
  const base = PIXI.BaseTexture.from(`/assets/fx/${name}.png`, { scaleMode: PIXI.SCALE_MODES.NEAREST });
  const build = (): void => {
    const cell = base.height; // strip horizontal de células quadradas
    textures.set(name, Array.from({ length: frames }, (_, i) =>
      new PIXI.Texture(base, new PIXI.Rectangle(i * cell, 0, cell, cell))));
  };
  if (base.valid) build();
  else base.once('loaded', build);
}

// null enquanto o PNG não carregou (ou se o efeito não tem sprite desenhado)
export function fxTex(name: string): Texture | null {
  return textures.get(name)?.[0] ?? null;
}

export function fxFrames(name: string): Texture[] | null {
  return textures.get(name) ?? null;
}
